import { AppThunk, RootState } from '../store'
import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { fetchAndActivate, getValue } from 'firebase/remote-config'

import { remoteConfig } from '@/services/firebase/remoteConfig'
import { User } from '@/interfaces/User'
interface FirebaseRemoteConfig {
  parameters: {
    allowedEmails: Parameter<string[]>
    allowedSchools: Parameter<string[]>
    enabled: Parameter<boolean>
  };
}

interface Parameter<T> {
  defaultValue: T
  description: string
}


enum FeatureFlags {
  FEEDBACK_WIDGET = 'feedback_widget',
  IS_ATTACHMENTS_MANAGEMENT_ENABLED = 'is_attachments_management_enabled',
  IS_PERFORMANCE_MONITORING_ENABLED = 'is_performance_monitoring_enabled',
  IS_SSO_GOOGLE_ENABLED = 'is_sso_google_enabled',
  IS_SSO_MICROSOFT_ENABLED = 'is_sso_microsoft_enabled',
}

interface FeatureFlagsState {
  areFeatureFlagsLoaded: boolean
  isFeedbackWidgetEnabled: boolean
  isPerformanceMonitoringEnabled: boolean
  isSSOGoogleEnabled: boolean
  isSSOMicrosoftEnabled: boolean
}

const initialState: FeatureFlagsState = {
  areFeatureFlagsLoaded: false,
  isFeedbackWidgetEnabled: false,
  isPerformanceMonitoringEnabled: false,
  isSSOGoogleEnabled: false,
  isSSOMicrosoftEnabled: false,
}

const featureFlagsSlice = createSlice({
  initialState,
  name: 'featureFlags',
  reducers: {
    getFeatureFlagsFailure(state, action: PayloadAction<string>) {
      // Carry on with every flag at its default (off) rather than holding the
      // whole app on the loading screen when Firebase can't be reached.
      state.areFeatureFlagsLoaded = true
      console.log('Failure to fetch feature flags.')
      console.log(action.payload)
    },
    getFeatureFlagsStart(state) {
      state.areFeatureFlagsLoaded = false
    },
    getFeatureFlagsSuccess(state, action: PayloadAction<Omit<FeatureFlagsState, 'areFeatureFlagsLoaded'>>) {
      state.isFeedbackWidgetEnabled = action.payload.isFeedbackWidgetEnabled
      state.isPerformanceMonitoringEnabled = action.payload.isPerformanceMonitoringEnabled
      state.isSSOGoogleEnabled = action.payload.isSSOGoogleEnabled
      state.isSSOMicrosoftEnabled = action.payload.isSSOMicrosoftEnabled
      state.areFeatureFlagsLoaded = true
    },
  },
})

export const { getFeatureFlagsFailure, getFeatureFlagsSuccess } = featureFlagsSlice.actions
export const areFeatureFlagsLoadedSelector = (state: RootState): boolean => state.featureFlags.areFeatureFlagsLoaded
export const isPerformanceMonitoringEnabledSelector = (state: RootState): boolean => state.featureFlags.isPerformanceMonitoringEnabled
export const isSSOGoogleEnabledSelector = (state: RootState): boolean => state.featureFlags.isSSOGoogleEnabled
export const isSSOMicrosoftEnabledSelector = (state: RootState): boolean => state.featureFlags.isSSOMicrosoftEnabled

export const getAllowed = (flagString: string, user?: User | null):boolean => {
  const flagJSON = JSON.parse(flagString) as FirebaseRemoteConfig
  if (!user) return flagJSON.parameters.enabled.defaultValue

  return flagJSON.parameters.allowedEmails.defaultValue.includes(user.email) || flagJSON.parameters.enabled.defaultValue
}

const FLAGS_TIMEOUT_MS = 4000

export const getFeatureFlags = (): AppThunk => async (dispatch, getState) => {
  // Everything here defaults to off, so a slow or blocked Firebase just means waiting less, never being stuck.
  const timer = setTimeout(() => dispatch(getFeatureFlagsFailure('Timed out')), FLAGS_TIMEOUT_MS)
  try {
    const rc = await remoteConfig()
    const userState = getState().user

    if (rc) {
      rc.settings.minimumFetchIntervalMillis = 0
      rc.defaultConfig = {
        [FeatureFlags.IS_ATTACHMENTS_MANAGEMENT_ENABLED]: false,
        [FeatureFlags.FEEDBACK_WIDGET]: '{"parameters": {"enabled": {"defaultValue": false,"description": "Controls whether the feature is enabled or not."},"allowedEmails": {"defaultValue": [],"description": "List of emails allowed to access the feature."},"allowedSchools": {"defaultValue": [],"description": "List of school ids allowed to access the feature."}}}',
        [FeatureFlags.IS_PERFORMANCE_MONITORING_ENABLED]: false,
        [FeatureFlags.IS_SSO_GOOGLE_ENABLED]: false,
        [FeatureFlags.IS_SSO_MICROSOFT_ENABLED]: false,
      }

      await fetchAndActivate(rc)
      const rcFeedbackWidgetString = getValue(rc, FeatureFlags.FEEDBACK_WIDGET).asString()
      const isFeedbackWidgetEnabled = getAllowed(rcFeedbackWidgetString, userState.user)
      const isPerformanceMonitoringEnabled = getValue(rc, FeatureFlags.IS_PERFORMANCE_MONITORING_ENABLED).asBoolean()
      const isSSOGoogleEnabled = getValue(rc, FeatureFlags.IS_SSO_GOOGLE_ENABLED).asBoolean()
      const isSSOMicrosoftEnabled = getValue(rc, FeatureFlags.IS_SSO_MICROSOFT_ENABLED).asBoolean()

      dispatch(
        getFeatureFlagsSuccess({
          isFeedbackWidgetEnabled,
          isPerformanceMonitoringEnabled,
          isSSOGoogleEnabled,
          isSSOMicrosoftEnabled,
        }),
      )
    } else {
      // Firebase says this browser can't run it (no usable IndexedDB)
      dispatch(getFeatureFlagsFailure('Not supported in this browser'))
    }
  } catch (err: any) {
    console.log(err)
    dispatch(getFeatureFlagsFailure(err.message))
  } finally {
    clearTimeout(timer)
  }
}

export default featureFlagsSlice.reducer
